import React, { useState, useEffect, useRef } from 'react';
import {
  getSettings, saveSettings,
  uploadBrandingImage, fetchBrandingImageFromUrl,
  getServices, addService, removeService,
  getLeadStatuses, createLeadStatus, updateLeadStatus, deleteLeadStatus,
  getLeadSources, createLeadSource, updateLeadSource, deleteLeadSource,
  getUsers, createUser, updateUser, deleteUser,
  adminSetPassword, adminGenerateResetLink,
  changePassword, getRoles, sendTestEmail,
} from '../utils/api';
import { Toggle, Modal } from '../components/UI';
import RolesManager from '../components/RolesManager';
import DomainCard from '../components/DomainCard';
import MetaConnectCard from '../components/MetaConnectCard';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';

function PasswordInput({ value, onChange, placeholder = 'Min 8 characters' }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input className="form-input" type={show ? 'text' : 'password'} value={value} onChange={onChange} placeholder={placeholder} style={{ paddingRight: 36 }} />
      <button type="button" onClick={() => setShow(s => !s)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 0, fontSize: 15 }}>
        <i className={`ti ${show ? 'ti-eye-off' : 'ti-eye'}`} />
      </button>
    </div>
  );
}

// Logo/favicon field — supports either a direct file upload or "paste a URL"
// (fetched + re-hosted server-side by api/config/image_ingest.php).
function BrandingImageField({ kind, label, value, onUploaded, toast }) {
  const fileRef = useRef();
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const r = await uploadBrandingImage(kind, file);
      onUploaded(r.data.url);
      toast(`${label} updated!`);
    } catch (err) { toast(err.response?.data?.error || `Failed to upload ${label.toLowerCase()}`); }
    finally { setBusy(false); }
  };

  const handleFetchUrl = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      const r = await fetchBrandingImageFromUrl(kind, url.trim());
      onUploaded(r.data.url);
      setUrl('');
      toast(`${label} updated!`);
    } catch (err) { toast(err.response?.data?.error || 'Could not fetch that URL'); }
    finally { setBusy(false); }
  };

  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        {value
          ? <img src={value} alt={label} style={{ height: 32, maxWidth: 100, objectFit: 'contain', border: '0.5px solid var(--gray-200)', borderRadius: 6, padding: 2 }} />
          : <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>No {label.toLowerCase()} set</span>}
        <button type="button" className="btn btn-outline btn-sm" onClick={() => fileRef.current?.click()} disabled={busy}>Upload</button>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" style={{ display: 'none' }} onChange={handleFile} />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input className="form-input" value={url} onChange={e => setUrl(e.target.value)} placeholder="...or paste an image URL" style={{ flex: 1 }} />
        <button type="button" className="btn btn-outline btn-sm" onClick={handleFetchUrl} disabled={busy || !url.trim()}>Fetch</button>
      </div>
    </div>
  );
}

// Read-only value with a copy button (webhook/cron URLs the client pastes elsewhere).
function CopyRow({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 3 }}>{label}</div>
      <div style={{ fontFamily: 'monospace', fontSize: 12, background: 'var(--gray-50)', padding: '8px 12px', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ wordBreak: 'break-all' }}>{value}</span>
        <button type="button" className="btn btn-outline btn-sm" onClick={copy} style={{ flexShrink: 0, padding: '3px 8px' }}>
          <i className={`ti ${copied ? 'ti-check' : 'ti-copy'}`} />
        </button>
      </div>
    </div>
  );
}

const TIMEZONES = [
  'UTC', 'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Karachi', 'Asia/Dhaka',
  'Asia/Shanghai', 'Asia/Tokyo', 'Asia/Jakarta', 'Asia/Riyadh',
  'Europe/London', 'Europe/Berlin', 'Europe/Paris', 'Europe/Moscow',
  'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Cairo',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Sao_Paulo', 'America/Mexico_City',
  'Australia/Sydney', 'Australia/Perth', 'Pacific/Auckland',
];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'AED', 'AUD', 'CAD', 'SGD', 'JPY', 'CNY', 'ZAR', 'NGN', 'BRL', 'MXN'];

// Quick-fill presets for the "own SMTP" host/port/encryption fields — picking
// one just pre-fills those three inputs, which stay editable either way.
// "Custom" leaves them as-is so any other provider (a hosting mailbox,
// SendGrid/Mailgun SMTP, an internal relay, etc.) can be typed in directly.
const SMTP_PRESETS = [
  { key: 'gmail',   label: 'Gmail',                 host: 'smtp.gmail.com',      port: 587, encryption: 'tls' },
  { key: 'outlook', label: 'Outlook / Office 365',   host: 'smtp.office365.com',  port: 587, encryption: 'tls' },
  { key: 'zoho',    label: 'Zoho Mail',              host: 'smtp.zoho.com',       port: 465, encryption: 'ssl' },
  { key: 'yahoo',   label: 'Yahoo Mail',             host: 'smtp.mail.yahoo.com', port: 587, encryption: 'tls' },
  { key: 'custom',  label: 'Custom SMTP server…',    host: '',                    port: 587, encryption: 'tls' },
];

// Shared CRUD list for lead_statuses / lead_sources — a color swatch, name,
// sort order, per-row flag checkboxes (which flags depend on the table), and
// a remove button, plus an "add new" row. Mirrors the plainer Services list
// above it, extended for the extra fields these tables carry.
function LookupManager({ title, items, onCreate, onUpdate, onDelete, flagFields }) {
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#6b7280');

  const add = async () => {
    if (!newName.trim()) return;
    await onCreate({ name: newName.trim(), color: newColor });
    setNewName(''); setNewColor('#6b7280');
  };

  return (
    <div>
      <div className="settings-section-title">{title}</div>
      {items.map(item => (
        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '0.5px solid var(--gray-100)', flexWrap: 'wrap' }}>
          <input type="color" value={item.color} onChange={e => onUpdate(item.id, { color: e.target.value })} style={{ width: 28, height: 28, padding: 0, border: 'none', borderRadius: 6, cursor: 'pointer' }} title="Color" />
          <input className="form-input" value={item.name} onChange={e => onUpdate(item.id, { name: e.target.value })} style={{ flex: '1 1 140px', maxWidth: 180 }} />
          <input className="form-input" type="number" value={item.sort_order} onChange={e => onUpdate(item.id, { sort_order: Number(e.target.value) })} style={{ width: 60 }} title="Sort order" />
          {flagFields.map(f => (
            <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--gray-600)', whiteSpace: 'nowrap' }} title={f.hint}>
              <input type="checkbox" checked={!!Number(item[f.key])} onChange={e => onUpdate(item.id, { [f.key]: e.target.checked })} />
              {f.label}
            </label>
          ))}
          <button className="btn btn-outline btn-sm" onClick={() => onDelete(item.id)} title="Remove" style={{ padding: '4px 8px', color: 'var(--red)', marginLeft: 'auto' }}>
            <i className="ti ti-trash" />
          </button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
        <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)} style={{ width: 28, height: 28, padding: 0, border: 'none', borderRadius: 6, cursor: 'pointer' }} />
        <input className="form-input" value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} placeholder={`Add ${title.toLowerCase()}...`} style={{ maxWidth: 220 }} />
        <button className="btn btn-outline btn-sm" onClick={add}>Add</button>
      </div>
    </div>
  );
}

export default function Settings({ toast }) {
  const { user: me, can } = useAuth();
  const { branding } = useBranding();
  const canViewUsers     = can('users.view') || can('users.manage');
  const canManageUsers   = can('users.manage');
  const canManageSettings = can('settings.manage');
  const canManageRoles   = can('roles.manage');

  const [settings, setSettings] = useState({});
  const [services, setServices] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [newService, setNewService] = useState('');
  const [newUser, setNewUser] = useState({ name: '', email: '', role_id: '' });
  const [setupUrl, setSetupUrl] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [pwdModal, setPwdModal] = useState(null);     // user to set password for
  const [pwdValue, setPwdValue] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);
  const [urlCopied, setUrlCopied] = useState(false);

  // Change my password
  const [changePwd, setChangePwd] = useState({ old: '', new: '', confirm: '' });
  const [changingPwd, setChangingPwd] = useState(false);

  const reloadLeadStatuses = () => getLeadStatuses().then(r => setLeadStatuses(r.data)).catch(() => {});
  const reloadLeadSources  = () => getLeadSources().then(r => setLeadSources(r.data)).catch(() => {});

  useEffect(() => {
    getSettings().then(r => setSettings(r.data)).catch(() => {});
    getServices().then(r => setServices(r.data)).catch(() => {});
    reloadLeadStatuses();
    reloadLeadSources();
    // Users power both the Team list and the default-assignee dropdown.
    if (canViewUsers || canManageSettings) {
      getUsers().then(r => setUsers(r.data)).catch(() => {});
    }
    // Roles populate the invite/edit dropdown; the list endpoint allows this
    // for anyone who can manage users.
    if (canViewUsers) {
      getRoles().then(r => setRoles(r.data)).catch(() => {});
    }
  }, [canViewUsers, canManageSettings]);

  const set = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  // Secrets are write-only: the API returns them blank plus a __secrets_set map
  // saying which ones HAVE a stored value, so we can show "configured" and a
  // "leave blank to keep" placeholder without ever holding the secret client-side.
  const secretsSet = settings.__secrets_set || {};
  const secretSet = (key) => secretsSet[key]
    ? <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, color: 'var(--green)' }}>✓ configured</span>
    : null;
  const secretPlaceholder = (key) => secretsSet[key] ? '•••••••• (leave blank to keep)' : '';

  // Email mode drives which block of the Outgoing Email card shows. Mirrors the
  // backend default in resolveMailCreds(): a tenant that already has an app
  // password is on its own SMTP, otherwise the platform relay.
  const emailMode = settings.email_mode || (secretsSet.gmail_app_password ? 'own' : 'platform');

  // Which preset the current smtp_host matches, so the Provider dropdown
  // reflects saved/edited values instead of always resetting to Gmail.
  const smtpPreset = SMTP_PRESETS.find(p => p.key !== 'custom' && p.host === (settings.smtp_host || 'smtp.gmail.com'))?.key || 'custom';

  const regenerateCronSecret = () => {
    const bytes = new Uint8Array(24);
    window.crypto.getRandomValues(bytes);
    set('cron_secret', Array.from(bytes, b => b.toString(16).padStart(2, '0')).join(''));
    toast('New secret generated — click Save Changes to apply');
  };

  // Lead statuses/sources persist immediately per-row (unlike the rest of this
  // page, which batches into one "Save Changes") — a full reload after every
  // mutation keeps exclusive flags (is_default etc., singleton server-side) in
  // sync across rows without hand-rolling that logic twice on the client.
  const handleCreateLeadStatus = async (data) => {
    try { await createLeadStatus(data); await reloadLeadStatuses(); toast('Status added!'); }
    catch (e) { toast(e.response?.data?.error || 'Failed to add status'); }
  };
  const handleUpdateLeadStatus = async (id, data) => {
    try { await updateLeadStatus(id, data); await reloadLeadStatuses(); }
    catch { toast('Failed to update status'); }
  };
  const handleDeleteLeadStatus = async (id) => {
    try { await deleteLeadStatus(id); await reloadLeadStatuses(); toast('Status removed'); }
    catch { toast('Failed to remove status'); }
  };
  const handleCreateLeadSource = async (data) => {
    try { await createLeadSource(data); await reloadLeadSources(); toast('Source added!'); }
    catch (e) { toast(e.response?.data?.error || 'Failed to add source'); }
  };
  const handleUpdateLeadSource = async (id, data) => {
    try { await updateLeadSource(id, data); await reloadLeadSources(); }
    catch { toast('Failed to update source'); }
  };
  const handleDeleteLeadSource = async (id) => {
    try { await deleteLeadSource(id); await reloadLeadSources(); toast('Source removed'); }
    catch { toast('Failed to remove source'); }
  };

  const saveAll = async () => {
    setSaving(true);
    try { await saveSettings(settings); toast('Settings saved!'); } catch { toast('Save failed'); } finally { setSaving(false); }
  };

  // Sends a test message to my own account email using whatever's currently
  // in the form — including unsaved edits — so an SMTP mistake is caught
  // before Save Changes. A blank password field is sent as-is; the server
  // falls back to the stored one, same "leave blank to keep" convention as
  // the rest of this form.
  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      await sendTestEmail(settings);
      toast(`Test email sent to ${me?.email || 'your inbox'} — check it arrived.`);
    } catch (e) { toast(e.response?.data?.error || 'Test email failed to send'); }
    finally { setTestingEmail(false); }
  };

  const handleAddService = async () => {
    if (!newService.trim()) return;
    try { await addService(newService.trim()); setServices(s => [...s, { name: newService.trim() }]); setNewService(''); toast('Service added!'); } catch { toast('Failed to add service'); }
  };

  const handleRemoveService = async (name) => {
    try { await removeService(name); setServices(s => s.filter(x => x.name !== name)); toast('Service removed'); } catch { toast('Failed to remove'); }
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email) { toast('Name and email required'); return; }
    try {
      const r = await createUser(newUser);
      if (r.data.setup_url) setSetupUrl(r.data.setup_url);
      getUsers().then(res => setUsers(res.data)).catch(() => {});
      setNewUser({ name: '', email: '', role_id: '' });
      toast('User created!');
    } catch (e) { toast(e.response?.data?.error || 'Failed to create user'); }
  };

  const handleSaveUser = async () => {
    if (!editUser.name || !editUser.email) { toast('Name and email required'); return; }
    setSavingUser(true);
    try {
      const r = await updateUser(editUser.id, editUser);
      setUsers(u => u.map(x => x.id === editUser.id ? r.data : x));
      setEditUser(null);
      toast('User updated!');
    } catch { toast('Failed to update user'); } finally { setSavingUser(false); }
  };

  const handleDeactivate = async (id) => {
    try {
      await deleteUser(id);
      setUsers(u => u.map(x => x.id === id ? { ...x, is_active: '0' } : x));
      setConfirmDelete(null);
      toast('User deactivated');
    } catch (e) {
      toast(e.response?.data?.error || 'Failed to deactivate user');
      setConfirmDelete(null);
    }
  };

  const handleSetPassword = async () => {
    if (!pwdValue) { toast('Enter a password'); return; }
    if (pwdValue.length < 8) { toast('Minimum 8 characters'); return; }
    if (pwdValue !== pwdConfirm) { toast('Passwords do not match'); return; }
    setSavingPwd(true);
    try {
      await adminSetPassword(pwdModal.id, pwdValue);
      setPwdModal(null); setPwdValue(''); setPwdConfirm('');
      toast(`Password updated for ${pwdModal.name}`);
    } catch (e) { toast(e.response?.data?.error || 'Failed to set password'); } finally { setSavingPwd(false); }
  };

  const handleCopyLink = async (u) => {
    setCopiedLink(u.id);
    try {
      const r = await adminGenerateResetLink(u.id);
      await navigator.clipboard.writeText(r.data.setup_url);
      toast('Setup link copied to clipboard!');
    } catch { toast('Failed to generate link'); }
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const copySetupUrl = () => {
    navigator.clipboard.writeText(setupUrl).then(() => {
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    });
  };

  const handleChangeMyPassword = async () => {
    if (!changePwd.old) { toast('Enter current password'); return; }
    if (changePwd.new.length < 8) { toast('New password must be at least 8 characters'); return; }
    if (changePwd.new !== changePwd.confirm) { toast('New passwords do not match'); return; }
    setChangingPwd(true);
    try {
      const r = await changePassword(changePwd.old, changePwd.new);
      // Changing your own password revokes every previously issued token
      // (server-side), including the one this tab is using — so swap in the
      // fresh token the server returns to avoid getting logged out mid-session.
      if (r.data?.token) localStorage.setItem('bc_token', r.data.token);
      setChangePwd({ old: '', new: '', confirm: '' });
      toast('Password changed successfully!');
    } catch (e) { toast(e.response?.data?.error || 'Failed to change password'); } finally { setChangingPwd(false); }
  };

  const isActive = (u) => u.is_active === 1 || u.is_active === '1' || u.is_active === undefined;

  return (
    <div>
      <div className="section-header">
        <div className="section-title">Settings</div>
        {canManageSettings && (
          <button className="btn btn-primary btn-sm" onClick={saveAll} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        )}
      </div>

      {/* ── Branding (settings.manage) ── */}
      {canManageSettings && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="settings-section-title">Branding</div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Brand Name</label>
              <input className="form-input" value={settings.brand_name || ''} onChange={e => set('brand_name', e.target.value)} placeholder="CRM" />
            </div>
            <div className="form-group">
              <label className="form-label">Primary Color</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="color" value={settings.primary_color || '#2563eb'} onChange={e => set('primary_color', e.target.value)} style={{ width: 40, height: 34, padding: 2, border: '0.5px solid var(--gray-200)', borderRadius: 6, cursor: 'pointer' }} />
                <input className="form-input" value={settings.primary_color || ''} onChange={e => set('primary_color', e.target.value)} placeholder="#2563eb" />
              </div>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Login Tagline</label>
            <input className="form-input" value={settings.login_tagline || ''} onChange={e => set('login_tagline', e.target.value)} placeholder="CRM — Sign in to continue" />
          </div>
          <div className="form-row">
            <BrandingImageField kind="logo" label="Logo" value={settings.logo_url} onUploaded={url => set('logo_url', url)} toast={toast} />
            <BrandingImageField kind="favicon" label="Favicon" value={settings.favicon_url} onUploaded={url => set('favicon_url', url)} toast={toast} />
          </div>
          <div className="settings-row">
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>Show "Powered by" credit</div>
              <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Small line on the login page crediting {settings.brand_name || 'your brand'}</div>
            </div>
            <Toggle value={settings.powered_by_enabled === '1'} onChange={v => set('powered_by_enabled', v ? '1' : '0')} />
          </div>
          <p style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 8 }}>
            Logo/favicon changes apply immediately. Everything else here needs "Save Changes" above.
          </p>
        </div>
      )}

      {/* ── Custom Domain (multi-tenant installs only — renders nothing otherwise) ── */}
      {canManageSettings && <DomainCard toast={toast} />}

      {/* ── Localization (settings.manage) ── */}
      {canManageSettings && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="settings-section-title">Localization</div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Timezone</label>
              <select className="form-input" value={settings.timezone || 'UTC'} onChange={e => set('timezone', e.target.value)}>
                {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Currency</label>
              <select className="form-input" value={settings.currency || 'USD'} onChange={e => set('currency', e.target.value)}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group" style={{ maxWidth: 260 }}>
            <label className="form-label">Date Format</label>
            <select className="form-input" value={settings.date_format || 'DD/MM/YYYY'} onChange={e => set('date_format', e.target.value)}>
              <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2026)</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2026)</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD (2026-12-31)</option>
            </select>
          </div>
        </div>
      )}

      {/* ── Lead Pipeline — statuses & sources (settings.manage) ── */}
      {canManageSettings && (
        <div className="card" style={{ marginBottom: 20 }}>
          <LookupManager
            title="Lead Statuses" items={leadStatuses}
            onCreate={handleCreateLeadStatus} onUpdate={handleUpdateLeadStatus} onDelete={handleDeleteLeadStatus}
            flagFields={[
              { key: 'is_closed', label: 'Closed', hint: 'Excluded from pending follow-up counts (e.g. Converted/Lost)' },
              { key: 'is_default', label: 'Default', hint: 'Default status for manually-created leads' },
              { key: 'is_default_auto', label: 'Auto-default', hint: 'Default status for auto-captured leads (ads/webhooks)' },
              { key: 'triggers_nc_followup', label: 'Day-2 follow-up', hint: 'Triggers the automated Not-Connected follow-up email' },
            ]}
          />
          <div style={{ height: 1, background: 'var(--gray-100)', margin: '20px 0' }} />
          <LookupManager
            title="Lead Sources" items={leadSources}
            onCreate={handleCreateLeadSource} onUpdate={handleUpdateLeadSource} onDelete={handleDeleteLeadSource}
            flagFields={[
              { key: 'is_default', label: 'Default', hint: 'Default source in the Add Lead form' },
              { key: 'is_auto_capture', label: 'Auto-capture', hint: 'Ads/webhook sources — new leads get the auto-default status' },
            ]}
          />
        </div>
      )}

      {/* ── Team Members (users.view / users.manage) ── */}
      {canViewUsers && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="settings-section-title">Team Members</div>
          {users.map(u => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '0.5px solid var(--gray-100)', opacity: isActive(u) ? 1 : 0.45 }}>
              <div className="avatar">{u.avatar_initials}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>{u.name}</div>
                <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{u.role_name || u.role} · {u.email}</div>
              </div>
              <span className="status-badge s-converted">{u.role_name || u.role}</span>
              {!isActive(u) && <span className="status-badge s-lost">Inactive</span>}

              {canManageUsers && (
                <>
                  <button className="btn btn-outline btn-sm" onClick={() => setEditUser({ ...u })} title="Edit user" style={{ padding: '4px 8px' }}>
                    <i className="ti ti-edit" />
                  </button>
                  <button className="btn btn-outline btn-sm" onClick={() => { setPwdModal(u); setPwdValue(''); setPwdConfirm(''); }} title="Set password" style={{ padding: '4px 8px' }}>
                    <i className="ti ti-key" />
                  </button>
                  <button className="btn btn-outline btn-sm" onClick={() => handleCopyLink(u)} title="Copy setup / reset link" style={{ padding: '4px 8px' }}>
                    <i className={`ti ${copiedLink === u.id ? 'ti-check' : 'ti-link'}`} />
                  </button>
                  {isActive(u) && u.id !== me?.id && (
                    <button className="btn btn-outline btn-sm" onClick={() => setConfirmDelete(u)} title="Deactivate user" style={{ padding: '4px 8px', color: 'var(--red)' }}>
                      <i className="ti ti-user-off" />
                    </button>
                  )}
                </>
              )}
            </div>
          ))}

          {/* Invite new user */}
          {canManageUsers && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Invite Team Member</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input className="form-input" value={newUser.name} onChange={e => setNewUser(u => ({ ...u, name: e.target.value }))} placeholder="Full Name" style={{ flex: 1, minWidth: 140 }} />
              <input className="form-input" value={newUser.email} onChange={e => setNewUser(u => ({ ...u, email: e.target.value }))} placeholder="Email" style={{ flex: 1, minWidth: 160 }} />
              <select className="form-input" value={newUser.role_id} onChange={e => setNewUser(u => ({ ...u, role_id: e.target.value }))} style={{ width: 140 }}>
                <option value="">Role…</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <button className="btn btn-primary btn-sm" onClick={handleAddUser}>Add User</button>
            </div>
          </div>
          )}

          {/* Setup URL banner after user creation */}
          {setupUrl && (
            <div style={{ marginTop: 14, background: 'var(--green-light)', border: '0.5px solid #bbf7d0', borderRadius: 8, padding: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--green)', marginBottom: 6 }}>
                <i className="ti ti-check" /> User created — share this setup link with them
              </div>
              <div style={{ fontSize: 11, color: 'var(--gray-600)', background: 'var(--white)', border: '0.5px solid var(--gray-200)', borderRadius: 5, padding: '7px 10px', wordBreak: 'break-all', marginBottom: 8 }}>
                {setupUrl}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-outline btn-sm" onClick={copySetupUrl}>
                  <i className={`ti ${urlCopied ? 'ti-check' : 'ti-copy'}`} /> {urlCopied ? 'Copied!' : 'Copy Link'}
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => setSetupUrl('')} style={{ color: 'var(--gray-400)' }}>Dismiss</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Roles & Permissions (roles.manage) ── */}
      {canManageRoles && <RolesManager toast={toast} />}

      {/* ── Lead Assignment (settings.manage) ── */}
      {canManageSettings && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="settings-section-title">Lead Assignment</div>
          <div className="form-group" style={{ maxWidth: 300 }}>
            <label className="form-label">Default Lead Assignee</label>
            <select className="form-input" value={settings.default_assigned_to || ''} onChange={e => set('default_assigned_to', e.target.value)}>
              <option value="">— Unassigned —</option>
              {users.filter(u => u.is_active === undefined || Number(u.is_active) === 1)
                    .map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <p style={{ fontSize: 12, color: 'var(--gray-400)' }}>New leads that arrive without an assignee of their own (manual add, CSV import, Meta Ads, form submissions with no per-form assignee) go to this person automatically.</p>
        </div>
      )}

      {/* ── Outgoing Email (settings.manage) ── */}
      {canManageSettings && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="settings-section-title">Outgoing Email</div>
          <div className="form-group" style={{ maxWidth: 420 }}>
            <label className="form-label">How should your emails be sent?</label>
            <select className="form-input" value={emailMode} onChange={e => set('email_mode', e.target.value)}>
              <option value="platform">Use the platform mail service (recommended)</option>
              <option value="own">Advanced — use my own SMTP</option>
            </select>
          </div>

          {emailMode === 'platform' ? (
            <>
              <p style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 12 }}>
                Emails go out through our mail service, shown as <strong>{settings.gmail_name || branding.brand_name}</strong>.
                Nothing to configure — replies come back to the address below.
              </p>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Display Name</label>
                  <input className="form-input" value={settings.gmail_name || ''} onChange={e => set('gmail_name', e.target.value)} placeholder={branding.brand_name} />
                </div>
                <div className="form-group">
                  <label className="form-label">Reply-To Email</label>
                  <input className="form-input" type="email" value={settings.reply_to_email || ''} onChange={e => set('reply_to_email', e.target.value)} placeholder="you@yourcompany.com" />
                </div>
              </div>
            </>
          ) : (
            <>
              <p style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 12 }}>
                Emails are sent through your own mail server, so they arrive from your address.
                Pick a provider below to fill in its server details, or choose Custom for any other host —
                your own domain's mailbox, a transactional-email provider's SMTP, or an internal relay.
              </p>

              <div className="form-group" style={{ maxWidth: 300 }}>
                <label className="form-label">Provider</label>
                <select
                  className="form-input"
                  value={smtpPreset}
                  onChange={e => {
                    const preset = SMTP_PRESETS.find(p => p.key === e.target.value);
                    if (preset && preset.key !== 'custom') {
                      set('smtp_host', preset.host);
                      set('smtp_port', String(preset.port));
                      set('smtp_encryption', preset.encryption);
                    }
                  }}
                >
                  {SMTP_PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label className="form-label">SMTP Host</label>
                  <input className="form-input" value={settings.smtp_host || ''} onChange={e => set('smtp_host', e.target.value)} placeholder="smtp.example.com" />
                </div>
                <div className="form-group">
                  <label className="form-label">Port</label>
                  <input className="form-input" type="number" value={settings.smtp_port || ''} onChange={e => set('smtp_port', e.target.value)} placeholder="587" />
                </div>
                <div className="form-group">
                  <label className="form-label">Encryption</label>
                  <select className="form-input" value={settings.smtp_encryption || 'tls'} onChange={e => set('smtp_encryption', e.target.value)}>
                    <option value="tls">STARTTLS (587)</option>
                    <option value="ssl">SSL / TLS (465)</option>
                    <option value="none">None</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Username / From Email</label>
                  <input className="form-input" type="email" value={settings.gmail_from || ''} onChange={e => set('gmail_from', e.target.value)} placeholder="you@yourcompany.com" />
                </div>
                <div className="form-group">
                  <label className="form-label">Password{secretSet('gmail_app_password')}</label>
                  <input className="form-input" type="password" value={settings.gmail_app_password || ''} onChange={e => set('gmail_app_password', e.target.value)} placeholder={secretPlaceholder('gmail_app_password')} />
                </div>
              </div>
              <div className="form-group" style={{ maxWidth: 300 }}>
                <label className="form-label">From Name</label>
                <input className="form-input" value={settings.gmail_name || ''} onChange={e => set('gmail_name', e.target.value)} placeholder={branding.brand_name} />
              </div>
              {smtpPreset === 'gmail' && (
                <p style={{ fontSize: 12, color: 'var(--gray-400)' }}>
                  For Gmail, "Password" must be an App Password — generate one in Google Account → Security → 2-Step Verification → App Passwords (a normal account password won't work).
                </p>
              )}
              <p style={{ fontSize: 12, color: 'var(--gray-400)' }}>
                Leave Username and Password blank only if your server is an internal relay that doesn't require authentication.
              </p>
            </>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4, paddingTop: 14, borderTop: '0.5px solid var(--gray-100)' }}>
            <button className="btn btn-outline btn-sm" onClick={handleTestEmail} disabled={testingEmail}>
              {testingEmail ? 'Sending…' : 'Send Test Email'}
            </button>
            <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>Sends a quick test to {me?.email || 'your own inbox'} using the settings above, including anything you've just typed — no need to save first.</span>
          </div>
        </div>
      )}

      {/* ── Facebook Lead Ads — self-contained (fetches and saves on its own,
             deliberately not part of the settings form, since connecting is an
             action rather than a field). Reaching this page already requires
             settings.view via the route guard; the card hides its own action
             buttons without settings.manage. ── */}
      <MetaConnectCard canManage={canManageSettings} toast={toast} />

      {/* ── WhatsApp (settings.manage) ── */}
      {canManageSettings && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="settings-section-title">WhatsApp</div>
          <p style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 12 }}>
            Auto-send your WhatsApp template to every new lead. Message content is edited in Auto-Response.
          </p>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">API URL</label>
              <input className="form-input" value={settings.waba_api_url || ''} onChange={e => set('waba_api_url', e.target.value)} placeholder="https://graph.facebook.com/v19.0/<phone-id>/messages" />
            </div>
            <div className="form-group">
              <label className="form-label">API Key{secretSet('waba_api_key')}</label>
              <input className="form-input" type="password" value={settings.waba_api_key || ''} onChange={e => set('waba_api_key', e.target.value)} placeholder={secretPlaceholder('waba_api_key')} />
            </div>
          </div>
          <div className="settings-row">
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>Send WhatsApp on new lead</div>
              <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Requires the API URL and key above</div>
            </div>
            <Toggle value={settings.auto_whatsapp_enabled === '1'} onChange={v => set('auto_whatsapp_enabled', v ? '1' : '0')} />
          </div>
        </div>
      )}

      {/* ── Scheduled Jobs (settings.manage) ── */}
      {canManageSettings && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="settings-section-title">Scheduled Jobs</div>
          <p style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 12 }}>
            These run automatically. The URLs below let you trigger them manually — the secret is unique to your account.
          </p>
          <CopyRow label="Follow-up emails" value={`${window.location.origin}/api/cron-followup?secret=YOUR_SECRET`} />
          <CopyRow label="Daily digest" value={`${window.location.origin}/api/cron-digest?secret=YOUR_SECRET`} />
          <div className="form-group" style={{ maxWidth: 420, marginTop: 4 }}>
            <label className="form-label">Cron Secret{secretSet('cron_secret')}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="form-input" type="password" value={settings.cron_secret || ''} onChange={e => set('cron_secret', e.target.value)} placeholder={secretPlaceholder('cron_secret')} />
              <button className="btn btn-outline btn-sm" onClick={regenerateCronSecret} style={{ flexShrink: 0 }}>Regenerate</button>
            </div>
            <p style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>Regenerating replaces it — remember to save.</p>
          </div>
        </div>
      )}

      {/* ── Services (settings.manage) ── */}
      {canManageSettings && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="settings-section-title">Services</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {services.map(s => (
              <span key={s.name} className="tag">{s.name} <span style={{ cursor: 'pointer' }} onClick={() => handleRemoveService(s.name)}>×</span></span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="form-input" value={newService} onChange={e => setNewService(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddService()} placeholder="Add service..." style={{ maxWidth: 220 }} />
            <button className="btn btn-outline btn-sm" onClick={handleAddService}>Add</button>
          </div>
        </div>
      )}

      {/* ── Notifications (settings.manage) ── */}
      {canManageSettings && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="settings-section-title">Notifications</div>
          {[
            { key: 'followup_digest_enabled', label: 'Daily follow-up digest', sub: "Email at 9 AM with today's tasks" },
            { key: 'auto_email_enabled', label: 'New lead auto-email', sub: 'Instant email to lead on capture' },
          ].map(({ key, label, sub }) => (
            <div key={key} className="settings-row">
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
                <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{sub}</div>
              </div>
              <Toggle value={settings[key] === '1'} onChange={v => set(key, v ? '1' : '0')} />
            </div>
          ))}
        </div>
      )}

      {/* ── Change My Password (all users) ── */}
      <div className="card">
        <div className="settings-section-title">Change My Password</div>
        <div style={{ maxWidth: 400 }}>
          <div className="form-group">
            <label className="form-label">Current Password</label>
            <PasswordInput value={changePwd.old} onChange={e => setChangePwd(p => ({ ...p, old: e.target.value }))} placeholder="Your current password" />
          </div>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <PasswordInput value={changePwd.new} onChange={e => setChangePwd(p => ({ ...p, new: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Confirm New Password</label>
            <PasswordInput value={changePwd.confirm} onChange={e => setChangePwd(p => ({ ...p, confirm: e.target.value }))} placeholder="Re-enter new password" />
          </div>
          {changePwd.new && changePwd.confirm && changePwd.new !== changePwd.confirm && (
            <p className="error-text" style={{ marginBottom: 8 }}>Passwords do not match</p>
          )}
          <button className="btn btn-primary btn-sm" onClick={handleChangeMyPassword} disabled={changingPwd || !changePwd.old || !changePwd.new || !changePwd.confirm}>
            {changingPwd ? 'Saving...' : 'Update Password'}
          </button>
        </div>
      </div>

      {/* ── Edit User Modal ── */}
      <Modal open={!!editUser} onClose={() => setEditUser(null)} title="Edit Team Member" width={460}>
        {editUser && (
          <>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="form-input" value={editUser.name} onChange={e => setEditUser(u => ({ ...u, name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={editUser.email} onChange={e => setEditUser(u => ({ ...u, email: e.target.value }))} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-input" value={editUser.role_id || ''} onChange={e => setEditUser(u => ({ ...u, role_id: e.target.value }))}>
                  <option value="">Role…</option>
                  {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
                <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={isActive(editUser)} onChange={e => setEditUser(u => ({ ...u, is_active: e.target.checked ? '1' : '0' }))} />
                  Active account
                </label>
              </div>
            </div>
            <div className="form-actions">
              <button className="btn btn-outline" onClick={() => setEditUser(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveUser} disabled={savingUser}>{savingUser ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </>
        )}
      </Modal>

      {/* ── Set Password Modal (admin sets password for another user) ── */}
      <Modal open={!!pwdModal} onClose={() => { setPwdModal(null); setPwdValue(''); setPwdConfirm(''); }} title={pwdModal ? `Set Password — ${pwdModal.name}` : ''} width={420}>
        {pwdModal && (
          <>
            <p style={{ fontSize: 13, color: 'var(--gray-600)', marginBottom: 16 }}>
              Set a new password for <strong>{pwdModal.name}</strong>. Make sure to share it with them securely, or use "Copy Link" to let them set their own.
            </p>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <PasswordInput value={pwdValue} onChange={e => setPwdValue(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <PasswordInput value={pwdConfirm} onChange={e => setPwdConfirm(e.target.value)} placeholder="Re-enter password" />
            </div>
            {pwdValue && pwdConfirm && pwdValue !== pwdConfirm && (
              <p className="error-text" style={{ marginBottom: 8 }}>Passwords do not match</p>
            )}
            <div className="form-actions">
              <button className="btn btn-outline" onClick={() => { setPwdModal(null); setPwdValue(''); setPwdConfirm(''); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSetPassword} disabled={savingPwd || !pwdValue || !pwdConfirm}>
                {savingPwd ? 'Setting...' : 'Set Password'}
              </button>
            </div>
          </>
        )}
      </Modal>

      {/* ── Deactivate Confirm Modal ── */}
      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Deactivate User" width={400}>
        {confirmDelete && (
          <>
            <p style={{ fontSize: 13, color: 'var(--gray-600)', marginBottom: 20 }}>
              Deactivate <strong>{confirmDelete.name}</strong>? They will no longer be able to log in. You can re-enable them from the edit form.
            </p>
            <div className="form-actions">
              <button className="btn btn-outline" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => handleDeactivate(confirmDelete.id)}>Deactivate</button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
